import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SMS_CONSENT } from "@/data/content";

const LOBS = [
  { value: "auto", label: "Auto Insurance" },
  { value: "home", label: "Home Insurance" },
  { value: "life", label: "Life Insurance" },
  { value: "commercial", label: "Commercial Insurance" },
  { value: "other", label: "Something else" },
];

const empty = { name: "", email: "", phone: "", line_of_business: "", message: "", sms_opt_in: false, website: "" };

export default function QuoteDialog({ open, onOpenChange, defaultLob = "", type = "quote" }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...empty, line_of_business: defaultLob });
  }, [open, defaultLob]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/submissions", {
        ...form, type,
        consent_version: SMS_CONSENT.version,
        consent_text: SMS_CONSENT.text,
      });
      toast.success("Thank you! Doug's team will reach out shortly.");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const isQuote = type === "quote";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-brand-cream border-brand-line p-0 overflow-hidden gap-0"
        data-testid="quote-dialog"
      >
        <div className="bg-brand-green px-7 py-6 text-brand-cream">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-sand">
            {isQuote ? "Free · No obligation" : "We'd love to hear from you"}
          </div>
          <DialogHeader className="mt-2 space-y-1 text-left">
            <DialogTitle className="font-serif text-3xl font-normal text-brand-cream">
              {isQuote ? "Request Your Quote" : "Send Us a Message"}
            </DialogTitle>
            <DialogDescription className="text-brand-cream/70 text-sm">
              Real people in Baton Rouge — we typically reply the same business day.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="px-7 py-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={form.website} onChange={(e) => set("website")(e.target.value)}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name">
              <Input data-testid="quote-name-input" value={form.name}
                onChange={(e) => set("name")(e.target.value)} placeholder="Jane Doe" />
            </Field>
            <Field label="Phone">
              <Input data-testid="quote-phone-input" value={form.phone}
                onChange={(e) => set("phone")(e.target.value)} placeholder="(225) 000-0000" />
            </Field>
          </div>

          <Field label="Email">
            <Input data-testid="quote-email-input" type="email" value={form.email}
              onChange={(e) => set("email")(e.target.value)} placeholder="you@email.com" />
          </Field>

          {isQuote && (
            <Field label="Line of business">
              <Select value={form.line_of_business} onValueChange={set("line_of_business")}>
                <SelectTrigger data-testid="quote-lob-select" className="bg-white border-brand-line">
                  <SelectValue placeholder="Choose coverage" />
                </SelectTrigger>
                <SelectContent>
                  {LOBS.map((l) => (
                    <SelectItem key={l.value} value={l.value} data-testid={`quote-lob-${l.value}`}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          <Field label={isQuote ? "Anything we should know? (optional)" : "Your message"}>
            <Textarea data-testid="quote-message-input" value={form.message}
              onChange={(e) => set("message")(e.target.value)} rows={3}
              placeholder="Tell us a little about what you're looking for..." />
          </Field>

          <label className="flex gap-3 items-start rounded-md border border-brand-line bg-white/60 p-3 cursor-pointer">
            <Checkbox data-testid="quote-sms-optin" checked={form.sms_opt_in}
              onCheckedChange={(v) => set("sms_opt_in")(!!v)} className="mt-0.5" />
            <span className="text-xs leading-relaxed text-brand-moss">
              I consent to receive SMS text messages from Doug Jones Insurance and Financial Planning Agency about my quote, policy, and
              account. Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out or HELP for help.
              Consent is not a condition of purchase. See our{" "}
              <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-brand-green">Privacy Policy</a> and{" "}
              <a href="/terms" target="_blank" rel="noreferrer" className="underline text-brand-green">Terms of Service</a>.
              <span className="block mt-1 font-mono uppercase tracking-wider text-[10px] text-brand-moss/70">A2P 10DLC compliant</span>
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="quote-submit-btn"
            className="w-full rounded-full bg-brand-clay text-white font-medium py-3.5 flex items-center justify-center gap-2 hover:bg-[#9e4c3e] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {isQuote ? "Get My Free Quote" : "Send Message"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-moss">{label}</Label>
      {children}
    </div>
  );
}
